<template>
  <div class="hierarchy-manager">
    <!-- Building Dialog -->
    <AddBuildingDialog
      v-model="showBuildingDialog"
      :form-data="newBuilding"
      :building-brick-classes="buildingBrickClasses"
      :saving="saving"
      @update:name="newBuilding.name = $event"
      @update:brick-class="newBuilding.brick_class = $event"
      @update:description="newBuilding.description = $event"
      @submit="addBuilding"
    />

    <!-- Edit Building Dialog -->
    <EditBuildingDialog
      v-model="showEditBuildingDialog"
      :form-data="editingBuilding"
      :building-brick-classes="buildingBrickClasses"
      :saving="saving"
      @update:name="editingBuilding.name = $event"
      @update:brick-class="editingBuilding.brick_class = $event"
      @update:description="editingBuilding.description = $event"
      @submit="updateBuilding"
    />

    <!-- Add Level Dialog -->
    <AddLevelDialog
      v-model="showLevelDialog"
      :form-data="newLevel"
      :selected-building="selectedBuilding"
      :level-brick-classes="levelBrickClasses"
      :saving="saving"
      @update:name="newLevel.name = $event"
      @update:brick-class="newLevel.brick_class = $event"
      @update:level-number="newLevel.level_number = $event"
      @update:description="newLevel.description = $event"
      @update-level-defaults="updateLevelDefaults"
      @submit="addLevel"
    />

    <!-- Edit Level Dialog -->
    <EditLevelDialog
      v-model="showEditLevelDialog"
      :form-data="editingLevel"
      :level-brick-classes="levelBrickClasses"
      :saving="saving"
      @update:name="editingLevel.name = $event"
      @update:brick-class="editingLevel.brick_class = $event"
      @update:level-number="editingLevel.level_number = $event"
      @update:description="editingLevel.description = $event"
      @submit="updateLevel"
    />

    <!-- Add Space Dialog -->
    <AddSpaceDialog
      v-model="showSpaceDialog"
      :form-data="newSpace"
      :selected-level="selectedLevel"
      :space-brick-classes="spaceBrickClasses"
      :saving="saving"
      @update:name="newSpace.name = $event"
      @update:brick-class="newSpace.brick_class = $event"
      @update:description="newSpace.description = $event"
      @update:area="newSpace.area = $event"
      @update:area-unit-id="newSpace.metadata.area_unit_id = $event"
      @update-space-defaults="updateSpaceDefaults"
      @submit="addSpace"
    />

    <!-- Edit Space Dialog -->
    <EditSpaceDialog
      v-model="showEditSpaceDialog"
      :form-data="editingSpace"
      :space-brick-classes="spaceBrickClasses"
      :saving="saving"
      @update:name="editingSpace.name = $event"
      @update:brick-class="editingSpace.brick_class = $event"
      @update:description="editingSpace.description = $event"
      @update:area="editingSpace.area = $event"
      @update:area-unit-id="editingSpace.metadata.area_unit_id = $event"
      @submit="updateSpace"
    />
  </div>
</template>

<script setup>
  import { ref, inject, computed } from 'vue';
  import { useQuasar } from 'quasar';
  import { useBrickClassOptions } from '@/composables/brick-schema';
  import { useBuildingApi } from '@/composables/brick-schema/api';
  import { useBuildingOperations } from '@/composables/brick-schema/operations';

  // Import dialog components
  import AddBuildingDialog from '../dialogs/building/AddBuildingDialog.vue';
  import EditBuildingDialog from '../dialogs/EditBuildingDialog.vue';
  import AddLevelDialog from '../dialogs/building/AddLevelDialog.vue';
  import EditLevelDialog from '../dialogs/EditLevelDialog.vue';
  import AddSpaceDialog from '../dialogs/building/AddSpaceDialog.vue';
  import EditSpaceDialog from '../dialogs/EditSpaceDialog.vue';

  // Props
  const props = defineProps({
    siteId: {
      type: [String, Number],
      required: true,
    },
  });

  // Emits
  const emit = defineEmits([
    'refresh-data',
    'building-added',
    'building-updated',
    'building-deleted',
    'level-added',
    'level-updated',
    'level-deleted',
    'space-added',
    'space-updated',
    'space-deleted',
  ]);

  // Inject shared context from parent
  const schemaContext = inject('schemaContext', null);

  // Quasar
  const $q = useQuasar();

  // Composables
  const {
    buildingBrickClasses,
    levelBrickClasses,
    spaceBrickClasses,
  } = useBrickClassOptions();

  const siteIdRef = computed(() => props.siteId);
  const buildingApi = useBuildingApi(siteIdRef);
  const buildingOps = useBuildingOperations();

  // State from composable
  const saving = buildingApi.saving;

  // ===================================
  // DIALOG VISIBILITY STATES
  // ===================================
  const showBuildingDialog = ref(false);
  const showEditBuildingDialog = ref(false);
  const showLevelDialog = ref(false);
  const showEditLevelDialog = ref(false);
  const showSpaceDialog = ref(false);
  const showEditSpaceDialog = ref(false);

  // ===================================
  // FORM DATA
  // ===================================
  const newBuilding = ref({
    name: '',
    description: '',
    brick_class: 'brick:Building',
  });

  const editingBuilding = ref({
    id: null,
    name: '',
    description: '',
    brick_class: 'brick:Building',
  });

  const newLevel = ref({
    brick_class: 'brick:Floor',
    name: '',
    level_number: '',
    description: '',
  });

  const editingLevel = ref({
    id: null,
    name: '',
    level_number: '',
    description: '',
    brick_class: 'brick:Floor',
  });

  const newSpace = ref({
    brick_class: 'brick:Space',
    name: '',
    description: '',
    area: null,
    metadata: {
      area_unit_id: null,
    },
  });

  const editingSpace = ref({
    id: null,
    name: '',
    description: '',
    brick_class: 'brick:Space',
    area: null,
    metadata: {
      area_unit_id: null,
    },
  });

  // ===================================
  // SELECTION STATE
  // ===================================
  const selectedBuilding = ref(null);
  const selectedLevel = ref(null);

  // ===================================
  // BUILDING OPERATIONS
  // ===================================

  // Show add building dialog
  function showAddBuildingDialog() {
    newBuilding.value = {
      name: '',
      description: '',
      brick_class: 'brick:Building',
    };
    showBuildingDialog.value = true;
  }

  // Add building
  async function addBuilding() {
    try {
      await buildingApi.createBuilding({
        site_id: props.siteId,
        ...newBuilding.value,
      });

      showBuildingDialog.value = false;
      newBuilding.value = {
        name: '',
        description: '',
        brick_class: 'brick:Building',
      };

      $q.notify({
        type: 'positive',
        message: 'Building added successfully',
      });

      emit('building-added');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add building: ${error.message}`,
      });
    }
  }

  // Edit building - open dialog
  function editBuilding(node) {
    editingBuilding.value = {
      id: node.data.id,
      name: node.data.name || node.label,
      description: node.data.description || '',
      brick_class: node.data.brick_class || 'brick:Building',
    };
    showEditBuildingDialog.value = true;
  }

  // Update building
  async function updateBuilding() {
    try {
      await buildingApi.updateBuilding(editingBuilding.value.id, {
        name: editingBuilding.value.name,
        description: editingBuilding.value.description,
        brick_class: editingBuilding.value.brick_class,
      });

      showEditBuildingDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Building updated successfully',
      });

      emit('building-updated');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update building: ${error.message}`,
      });
    }
  }

  // Delete building
  async function deleteBuilding(node) {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete building "${node.label}"? This will also delete all levels and spaces within it.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await buildingApi.deleteBuilding(node.data.id);

        $q.notify({
          type: 'positive',
          message: 'Building deleted successfully',
        });

        emit('building-deleted');
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete building: ${error.message}`,
        });
      }
    });
  }

  // ===================================
  // LEVEL OPERATIONS
  // ===================================

  // Show add level dialog
  function showAddLevelDialog(buildingNode) {
    selectedBuilding.value = buildingNode.data;
    newLevel.value = {
      brick_class: 'brick:Floor',
      name: '',
      level_number: '',
      description: '',
    };
    showLevelDialog.value = true;
  }

  // Update level defaults based on brick class selection
  function updateLevelDefaults(brickClass) {
    // Auto-populate name based on brick class if not already set
    if (!newLevel.value.name) {
      const classLabel = brickClass.replace('brick:', '').replace(/_/g, ' ');
      newLevel.value.name = classLabel;
    }
  }

  // Add level
  async function addLevel() {
    if (!selectedBuilding.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a building first',
      });
      return;
    }

    try {
      await buildingApi.createLevel(selectedBuilding.value.id, {
        ...newLevel.value,
      });

      showLevelDialog.value = false;
      newLevel.value = {
        brick_class: 'brick:Floor',
        name: '',
        level_number: '',
        description: '',
      };

      $q.notify({
        type: 'positive',
        message: 'Level added successfully',
      });

      emit('level-added');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add level: ${error.message}`,
      });
    }
  }

  // Edit level - open dialog
  function editLevel(node) {
    editingLevel.value = {
      id: node.data.id,
      name: node.data.name || node.label,
      level_number: node.data.level_number || '',
      description: node.data.description || '',
      brick_class: node.data.brick_class || 'brick:Floor',
    };
    showEditLevelDialog.value = true;
  }

  // Update level
  async function updateLevel() {
    try {
      await buildingApi.updateLevel(editingLevel.value.id, {
        name: editingLevel.value.name,
        level_number: editingLevel.value.level_number,
        description: editingLevel.value.description,
        brick_class: editingLevel.value.brick_class,
      });

      showEditLevelDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Level updated successfully',
      });

      emit('level-updated');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update level: ${error.message}`,
      });
    }
  }

  // Delete level
  async function deleteLevel(node) {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete level "${node.label}"? This will also delete all spaces within it.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await buildingApi.deleteLevel(node.data.id);

        $q.notify({
          type: 'positive',
          message: 'Level deleted successfully',
        });

        emit('level-deleted');
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete level: ${error.message}`,
        });
      }
    });
  }

  // ===================================
  // SPACE OPERATIONS
  // ===================================

  // Show add space dialog
  function showAddSpaceDialog(levelNode) {
    selectedLevel.value = levelNode.data;
    newSpace.value = {
      brick_class: 'brick:Space',
      name: '',
      description: '',
      area: null,
      metadata: {
        area_unit_id: null,
      },
    };
    showSpaceDialog.value = true;
  }

  // Update space defaults based on brick class selection
  function updateSpaceDefaults(brickClass) {
    // Auto-populate name based on brick class if not already set
    if (!newSpace.value.name) {
      const classLabel = brickClass.replace('brick:', '').replace(/_/g, ' ');
      newSpace.value.name = classLabel;
    }
  }

  // Add space
  async function addSpace() {
    if (!selectedLevel.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a level first',
      });
      return;
    }

    try {
      await buildingApi.createSpace(selectedLevel.value.id, {
        ...newSpace.value,
      });

      showSpaceDialog.value = false;
      newSpace.value = {
        brick_class: 'brick:Space',
        name: '',
        description: '',
        area: null,
        metadata: {
          area_unit_id: null,
        },
      };

      $q.notify({
        type: 'positive',
        message: 'Space added successfully',
      });

      emit('space-added');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add space: ${error.message}`,
      });
    }
  }

  // Edit space - open dialog
  function editSpace(node) {
    if (!node.data || !node.data.id) {
      $q.notify({
        type: 'negative',
        message: 'Unable to edit space - invalid data',
      });
      return;
    }

    editingSpace.value = {
      id: node.data.id,
      name: node.data.name || node.label,
      description: node.data.description || '',
      brick_class: node.data.brick_class || 'brick:Space',
      area: node.data.area || null,
      metadata: node.data.metadata || {
        area_unit_id: null,
      },
    };
    showEditSpaceDialog.value = true;
  }

  // Update space
  async function updateSpace() {
    try {
      await buildingApi.updateSpace(editingSpace.value.id, {
        name: editingSpace.value.name,
        description: editingSpace.value.description,
        brick_class: editingSpace.value.brick_class,
        area: editingSpace.value.area,
        metadata: editingSpace.value.metadata,
      });

      showEditSpaceDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Space updated successfully',
      });

      emit('space-updated');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update space: ${error.message}`,
      });
    }
  }

  // Delete space
  async function deleteSpace(node) {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete space "${node.label}"? This will also delete all equipment within it.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await buildingApi.deleteSpace(node.data.id);

        $q.notify({
          type: 'positive',
          message: 'Space deleted successfully',
        });

        emit('space-deleted');
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete space: ${error.message}`,
        });
      }
    });
  }

  // ===================================
  // EXPOSE PUBLIC METHODS
  // ===================================
  defineExpose({
    // Building operations
    showAddBuildingDialog,
    editBuilding,
    deleteBuilding,

    // Level operations
    showAddLevelDialog,
    editLevel,
    deleteLevel,

    // Space operations
    showAddSpaceDialog,
    editSpace,
    deleteSpace,
  });
</script>

<style scoped>
.hierarchy-manager {
  /* Container styles - invisible wrapper */
}
</style>
